"use client";

import { useEffect, useState, startTransition, useRef } from "react";
import Button from "@/components/ui/Button";
import Modal from "@/components/ui/Modal";
import { useAuth } from "@/contexts/AuthContext";
import {
  captureTelegramConnectClicked,
  captureTelegramConnectLinkFailed,
  captureTelegramConnectLinkReady,
  captureTelegramConnectPanelViewed,
  captureTelegramConnected,
  captureTelegramNearMeOptedIn,
  telegramConnectLinkFailReasonFromResponse,
  type TelegramConnectSurface,
} from "@/lib/analytics/captureTelegramConnect";
import { supabase } from "@/lib/supabase";
import {
  enableNearMeTelegramOptInAfterLink,
  isTelegramChatIdLinked,
} from "@/lib/notifications/near-me-telegram-preference-sync";

type TelegramNearMeConnectPanelProps = {
  /** Discovery near-me must be on before Telegram can connect. */
  nearMeOptIn?: boolean;
  /** When false, caller supplies the section heading. */
  showHeading?: boolean;
  className?: string;
  /** Compact density for modals. */
  compact?: boolean;
  /** Onboarding uses slightly larger heading type. */
  variant?: "default" | "onboarding";
  /**
   * Funnel surface for PostHog. Defaults: onboarding variant → onboarding; else profile.
   * Homepage modal must pass `homepage`.
   */
  surface?: TelegramConnectSurface;
};

function resolveTelegramConnectSurface(
  variant: "default" | "onboarding",
  surface?: TelegramConnectSurface,
): TelegramConnectSurface {
  if (surface) return surface;
  return variant === "onboarding" ? "onboarding" : "profile";
}

type LinkState = {
  chatId: string | null;
  linkedAt: string | null;
  notifyTelegram: boolean;
  optInAt: string | null;
  optOutAt: string | null;
};

const EMPTY_LINK: LinkState = {
  chatId: null,
  linkedAt: null,
  notifyTelegram: false,
  optInAt: null,
  optOutAt: null,
};

const LINK_POLL_INTERVAL_MS = 2000;
const LINK_POLL_MAX_MS = 90_000;

const DISCONNECT_TITLE = "Stop private Telegram alerts?";
const DISCONNECT_BODY =
  "We'll disconnect the Sell It Off bot from your chat. You won't get private nearby listing messages anymore.\n\nIf you follow a public listing channel (Lagos / Oyo), you'll still see those group posts.";

async function fetchTelegramLinkState(userId: string): Promise<LinkState> {
  const [profileRes, prefsRes] = await Promise.all([
    supabase
      .from("profiles")
      .select("telegram_chat_id, telegram_linked_at")
      .eq("id", userId)
      .maybeSingle(),
    supabase
      .from("notification_preferences")
      .select(
        "near_me_notify_telegram, near_me_telegram_opt_in_at, near_me_telegram_opt_out_at",
      )
      .eq("user_id", userId)
      .maybeSingle(),
  ]);

  if (profileRes.error) {
    console.error("[telegram_connect] profile:", profileRes.error);
  }
  if (prefsRes.error) {
    console.error("[telegram_connect] prefs:", prefsRes.error);
  }

  return {
    chatId: profileRes.data?.telegram_chat_id ?? null,
    linkedAt: profileRes.data?.telegram_linked_at ?? null,
    notifyTelegram: Boolean(prefsRes.data?.near_me_notify_telegram),
    optInAt: prefsRes.data?.near_me_telegram_opt_in_at ?? null,
    optOutAt: prefsRes.data?.near_me_telegram_opt_out_at ?? null,
  };
}

/**
 * Single control for private near-me Telegram DMs:
 * check → connect bot (and enable alerts); uncheck → confirm → disconnect.
 * Shared by Profile, onboarding, and homepage.
 */
export default function TelegramNearMeConnectPanel({
  nearMeOptIn = true,
  showHeading = true,
  className = "",
  compact = false,
  variant = "default",
  surface: surfaceProp,
}: TelegramNearMeConnectPanelProps) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const isOnboarding = variant === "onboarding";
  const surface = resolveTelegramConnectSurface(variant, surfaceProp);
  const [loading, setLoading] = useState(Boolean(userId));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<LinkState>(EMPTY_LINK);
  const [startCommand, setStartCommand] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">(
    "idle",
  );
  const [reloadToken, setReloadToken] = useState(0);
  const [awaitingLink, setAwaitingLink] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const hasLoadedOnceRef = useRef(false);
  const panelViewedFiredRef = useRef(false);
  const wasLinkedRef = useRef(false);
  const hasObservedLinkStateRef = useRef(false);
  const connectedFiredRef = useRef(false);
  const autoOptInFiredRef = useRef(false);

  const linked = isTelegramChatIdLinked(link.chatId);
  /** Checkbox reflects connect state (linked, or connect in progress). */
  const checked = linked || awaitingLink;

  useEffect(() => {
    if (!userId) {
      startTransition(() => {
        setLoading(false);
        hasLoadedOnceRef.current = false;
        panelViewedFiredRef.current = false;
        wasLinkedRef.current = false;
        hasObservedLinkStateRef.current = false;
        connectedFiredRef.current = false;
        autoOptInFiredRef.current = false;
        setLink(EMPTY_LINK);
        setAwaitingLink(false);
        setDisconnectOpen(false);
      });
      return;
    }

    let cancelled = false;
    startTransition(() => {
      if (!hasLoadedOnceRef.current) {
        setLoading(true);
      }
      setError(null);
    });

    void fetchTelegramLinkState(userId).then(async (next) => {
      if (cancelled) return;
      const nowLinked = isTelegramChatIdLinked(next.chatId);
      const justLinked =
        hasObservedLinkStateRef.current && nowLinked && !wasLinkedRef.current;

      if (justLinked && !connectedFiredRef.current) {
        connectedFiredRef.current = true;
        captureTelegramConnected({ surface });
      }

      let resolved = next;
      // Linked chat always means nearby private DMs are on (heal legacy linked+off).
      if (
        nowLinked &&
        nearMeOptIn &&
        !next.notifyTelegram &&
        next.chatId &&
        (justLinked || !autoOptInFiredRef.current)
      ) {
        autoOptInFiredRef.current = true;
        const enabled = await enableNearMeTelegramOptInAfterLink(supabase, {
          userId,
          telegramChatId: next.chatId,
          previous: {
            enabled: next.notifyTelegram,
            opt_in_at: next.optInAt,
            opt_out_at: next.optOutAt,
          },
        });
        if (cancelled) return;
        if (enabled.ok) {
          captureTelegramNearMeOptedIn({
            surface,
            method: justLinked ? "auto_link" : "toggle",
          });
          resolved = {
            ...next,
            notifyTelegram: enabled.patch.near_me_notify_telegram,
            optInAt: enabled.patch.near_me_telegram_opt_in_at,
            optOutAt: enabled.patch.near_me_telegram_opt_out_at,
          };
        }
      }

      hasObservedLinkStateRef.current = true;
      wasLinkedRef.current = nowLinked;
      startTransition(() => {
        setLink(resolved);
        if (nowLinked) {
          setStartCommand(null);
          setAwaitingLink(false);
        }
        setLoading(false);
        hasLoadedOnceRef.current = true;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [userId, reloadToken, surface, nearMeOptIn]);

  useEffect(() => {
    if (loading || !userId || linked || panelViewedFiredRef.current) return;
    panelViewedFiredRef.current = true;
    captureTelegramConnectPanelViewed(surface);
  }, [loading, userId, linked, surface]);

  useEffect(() => {
    if (!awaitingLink || !userId || linked) {
      return;
    }

    const startedAt = Date.now();
    const tick = () => {
      if (Date.now() - startedAt > LINK_POLL_MAX_MS) {
        setAwaitingLink(false);
        return;
      }
      setReloadToken((n) => n + 1);
    };

    const intervalId = window.setInterval(tick, LINK_POLL_INTERVAL_MS);

    const onFocusOrVisible = () => {
      if (document.visibilityState === "hidden") return;
      setReloadToken((n) => n + 1);
    };
    window.addEventListener("focus", onFocusOrVisible);
    document.addEventListener("visibilitychange", onFocusOrVisible);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
    };
  }, [awaitingLink, userId, linked]);

  const refresh = () => setReloadToken((n) => n + 1);

  const withAccessToken = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  };

  const handleConnect = async () => {
    if (!userId || !nearMeOptIn) return;
    captureTelegramConnectClicked(surface);
    setBusy(true);
    setError(null);
    setCopyState("idle");
    setAwaitingLink(true);
    try {
      const accessToken = await withAccessToken();
      if (!accessToken) {
        captureTelegramConnectLinkFailed({
          surface,
          reason: telegramConnectLinkFailReasonFromResponse({
            status: 0,
            hasSession: false,
          }),
        });
        setError("Sign in again to connect Telegram.");
        setAwaitingLink(false);
        return;
      }
      const res = await fetch("/api/telegram/user-link", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const json = (await res.json()) as {
        url?: string;
        startCommand?: string;
        error?: string;
      };
      if (!res.ok || !json.url) {
        captureTelegramConnectLinkFailed({
          surface,
          reason: telegramConnectLinkFailReasonFromResponse({
            status: res.status,
            hasSession: true,
          }),
        });
        setError(json.error || "Could not create Connect link.");
        setAwaitingLink(false);
        return;
      }
      captureTelegramConnectLinkReady(surface);
      if (json.startCommand) {
        setStartCommand(json.startCommand);
      }
      window.open(json.url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => refresh(), 1500);
    } catch (err) {
      console.error("[telegram_connect] connect:", err);
      captureTelegramConnectLinkFailed({
        surface,
        reason: "network",
      });
      setError("Could not open Telegram. Try again.");
      setAwaitingLink(false);
    } finally {
      setBusy(false);
    }
  };

  const handleCopyStartCommand = async () => {
    if (!startCommand) return;
    try {
      await navigator.clipboard.writeText(startCommand);
      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 2000);
    } catch {
      setCopyState("error");
    }
  };

  const handleDisconnect = async () => {
    if (!userId) return;
    setBusy(true);
    setError(null);
    try {
      const accessToken = await withAccessToken();
      if (!accessToken) {
        setError("Sign in again to disconnect Telegram.");
        return;
      }
      const res = await fetch("/api/telegram/user-link", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "disconnect" }),
      });
      const json = (await res.json()) as { error?: string };
      if (!res.ok) {
        setError(json.error || "Could not disconnect.");
        return;
      }
      wasLinkedRef.current = false;
      hasObservedLinkStateRef.current = true;
      connectedFiredRef.current = false;
      autoOptInFiredRef.current = false;
      panelViewedFiredRef.current = false;
      setLink(EMPTY_LINK);
      setAwaitingLink(false);
      setStartCommand(null);
      setDisconnectOpen(false);
    } catch (err) {
      console.error("[telegram_connect] disconnect:", err);
      setError("Could not disconnect. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleCheckboxChange = (wantOn: boolean) => {
    if (busy || !nearMeOptIn) return;

    if (wantOn) {
      if (linked || awaitingLink) return;
      void handleConnect();
      return;
    }

    // Turning off
    if (awaitingLink && !linked) {
      setAwaitingLink(false);
      setStartCommand(null);
      setError(null);
      return;
    }

    if (linked) {
      setDisconnectOpen(true);
    }
  };

  if (!userId) {
    return (
      <div className={className}>
        <p className="text-xs text-text-light">
          Sign in to connect Telegram for private nearby alerts.
        </p>
      </div>
    );
  }

  const heading = isOnboarding ? "Get nearby listings in Telegram" : "Telegram";
  const description = isOnboarding
    ? "Optional — check the box, open Telegram once, and tap Start. Nearby tips go to this private chat."
    : surface === "homepage" || compact
      ? "Private nearby tips in a bot chat — not the same as public listing channels."
      : "Private nearby listing tips in a bot chat — not the same as following public listing channels.";

  const helper = !nearMeOptIn
    ? "Turn on New listings near you first."
    : linked
      ? "Private nearby listing tips go to this Telegram chat."
      : awaitingLink
        ? null
        : "Check the box to connect Telegram for private nearby tips.";

  return (
    <div className={`space-y-3 ${className}`.trim()}>
      {showHeading ? (
        <div>
          <h3
            className={
              isOnboarding
                ? "text-base font-semibold text-foreground"
                : "text-sm font-semibold text-foreground"
            }
          >
            {heading}
          </h3>
          <p
            className={
              isOnboarding
                ? "mt-1 text-sm text-text-light"
                : "mt-0.5 text-xs text-text-light"
            }
          >
            {description}
          </p>
        </div>
      ) : null}

      {loading ? (
        <p className="text-xs text-text-light">Checking Telegram link…</p>
      ) : (
        <>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary disabled:opacity-60"
              checked={checked}
              disabled={busy || !nearMeOptIn}
              onChange={(e) => handleCheckboxChange(e.target.checked)}
            />
            <span className={isOnboarding ? "text-sm" : "text-sm"}>
              <span className="font-medium text-foreground">Telegram</span>
              {helper ? (
                <span className="block text-xs text-text-light mt-0.5">
                  {helper}
                </span>
              ) : null}
              {busy ? (
                <span className="block text-xs text-text-light mt-0.5">
                  {awaitingLink ? "Opening Telegram…" : "Working…"}
                </span>
              ) : null}
            </span>
          </label>

          {linked ? (
            <div className="ml-6 flex flex-wrap items-center gap-2">
              <span
                className={
                  isOnboarding
                    ? "text-sm font-medium text-primary-700"
                    : "text-xs font-medium text-primary-700"
                }
                role="status"
              >
                Connected
                {link.linkedAt
                  ? ` · ${new Date(link.linkedAt).toLocaleDateString()}`
                  : ""}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={refresh}
              >
                Refresh
              </Button>
            </div>
          ) : null}

          {!linked && awaitingLink ? (
            <div className="ml-6 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <p
                  className={
                    isOnboarding
                      ? "rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm text-foreground"
                      : "text-xs text-text-light"
                  }
                  role="status"
                  aria-live="polite"
                >
                  Waiting for you to tap <strong>Start</strong> in Telegram…
                  This page updates automatically when you&apos;re connected.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => {
                    setAwaitingLink(true);
                    refresh();
                  }}
                >
                  Refresh
                </Button>
              </div>
              {startCommand ? (
                <div className="rounded-md border border-border bg-surface px-3 py-2.5 space-y-2">
                  <p className="text-xs text-text-light">
                    If Telegram opens the chat but you don&apos;t get
                    &quot;You&apos;re connected&quot;, paste this into the bot
                    chat and send it (don&apos;t use the menu /start alone):
                  </p>
                  <code className="block break-all rounded bg-secondary/40 px-2 py-1.5 text-[11px] text-foreground">
                    {startCommand}
                  </code>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => void handleCopyStartCommand()}
                    >
                      {copyState === "copied" ? "Copied" : "Copy command"}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => {
                        setAwaitingLink(true);
                        refresh();
                      }}
                    >
                      I sent it — refresh
                    </Button>
                    {copyState === "error" ? (
                      <span className="text-xs text-red-600">
                        Couldn&apos;t copy — select the text above.
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      )}

      {error ? <p className="text-xs text-red-600">{error}</p> : null}

      <Modal
        isOpen={disconnectOpen}
        onClose={() => {
          if (!busy) setDisconnectOpen(false);
        }}
        title={DISCONNECT_TITLE}
        maxWidth="sm"
        renderAction
        confirmText="Disconnect"
        cancelText="Keep alerts"
        isSubmitting={busy}
        onCancel={() => {
          if (!busy) setDisconnectOpen(false);
        }}
        onConfirm={() => {
          void handleDisconnect();
        }}
        closeOnClickOutside={!busy}
      >
        <p className="text-sm text-text-light whitespace-pre-line">
          {DISCONNECT_BODY}
        </p>
      </Modal>
    </div>
  );
}
